const DEFAULT_API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_BACKEND ||
  process.env.REACT_APP_API_URL ||
  "";

const ID_KEYS = ["id", "_id", "advertiserId", "advertiser_id"];
const NAME_KEYS = ["name", "advertiser", "advertiser_name", "company", "company_name"];
const LOGO_URL_KEYS = [
  "logoUrl",
  "logo_url",
  "logo",
  "advertiserLogo",
  "advertiser_logo",
  "advertiser_logo_url",
  "advertiserLogoUrl",
  "company_logo",
  "companyLogo",
  "brand_logo",
  "brandLogo",
  "image_url",
  "imageUrl",
];
const LOGO_DRIVE_ID_KEYS = [
  "logoDriveFileId",
  "logo_drive_file_id",
  "advertiserLogoDriveFileId",
  "advertiser_logo_drive_file_id",
];
const LOGO_UPDATED_AT_KEYS = ["logoUpdatedAt", "logo_updated_at"];
const LOGO_ALT_TEXT_KEYS = ["logoAltText", "logo_alt_text"];
const HAS_LOGO_KEYS = ["hasLogo", "has_logo"];

const pickFirst = (record, keys) => {
  if (!record || typeof record !== "object") return undefined;
  for (const key of keys) {
    const value = record[key];
    const isObjectValue = value && typeof value === "object" && !(value instanceof Date);
    if (value !== undefined && value !== null && value !== "" && !isObjectValue) {
      return value;
    }
  }
  return undefined;
};

export const normalizeApiBase = (base = DEFAULT_API_BASE_URL) => {
  let normalized = String(base || "").trim().replace(/\/+$/, "");
  if (normalized.toLowerCase().endsWith("/api")) {
    normalized = normalized.slice(0, -4);
  }
  return normalized;
};

export const absoluteAdvertiserLogoUrl = (value, apiBase = DEFAULT_API_BASE_URL) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${normalizeApiBase(apiBase)}${raw.startsWith("/") ? raw : `/${raw}`}`;
};

export const appendAdvertiserLogoStamp = (value, stamp) => {
  const base = String(value || "").trim();
  if (!base) return "";
  const version = String(stamp || "").trim();
  if (!version) return base;
  return `${base}${base.includes("?") ? "&" : "?"}v=${encodeURIComponent(version)}`;
};

export const normalizeAdvertiserLogoMeta = (record = {}) => {
  const rawRecord =
    record && typeof record.raw === "object" && record.raw ? record.raw : null;

  const advertiserId =
    pickFirst(record, ID_KEYS) ??
    pickFirst(rawRecord, ID_KEYS) ??
    null;
  const advertiserName =
    pickFirst(record, NAME_KEYS) ??
    pickFirst(rawRecord, NAME_KEYS) ??
    "";
  const logoUrl = String(
    pickFirst(record, LOGO_URL_KEYS) ?? pickFirst(rawRecord, LOGO_URL_KEYS) ?? ""
  ).trim();
  const logoDriveFileId = String(
    pickFirst(record, LOGO_DRIVE_ID_KEYS) ??
      pickFirst(rawRecord, LOGO_DRIVE_ID_KEYS) ??
      ""
  ).trim();
  const logoUpdatedAt =
    pickFirst(record, LOGO_UPDATED_AT_KEYS) ??
    pickFirst(rawRecord, LOGO_UPDATED_AT_KEYS) ??
    "";
  const logoAltText = String(
    pickFirst(record, LOGO_ALT_TEXT_KEYS) ??
      pickFirst(rawRecord, LOGO_ALT_TEXT_KEYS) ??
      ""
  ).trim();
  const explicitHasLogo =
    typeof pickFirst(record, HAS_LOGO_KEYS) === "boolean"
      ? pickFirst(record, HAS_LOGO_KEYS)
      : typeof pickFirst(rawRecord, HAS_LOGO_KEYS) === "boolean"
        ? pickFirst(rawRecord, HAS_LOGO_KEYS)
        : undefined;

  return {
    ...record,
    raw: rawRecord,
    id: advertiserId,
    advertiserId,
    advertiserName,
    logoUrl,
    logoDriveFileId,
    logoUpdatedAt,
    logoAltText,
    hasLogo:
      typeof explicitHasLogo === "boolean"
        ? explicitHasLogo
        : Boolean(logoDriveFileId || logoUrl),
  };
};

export const buildAdvertiserLogoCandidates = (
  apiBase = DEFAULT_API_BASE_URL,
  ...advertiserCandidates
) => {
  const seen = new Set();
  const sources = [];

  advertiserCandidates.forEach((candidate) => {
    if (!candidate) return;

    const normalized = normalizeAdvertiserLogoMeta(candidate);
    const direct = appendAdvertiserLogoStamp(
      absoluteAdvertiserLogoUrl(normalized.logoUrl, apiBase),
      normalized.logoUpdatedAt
    );
    if (direct && !seen.has(direct)) {
      seen.add(direct);
      sources.push(direct);
    }

    if (normalized.advertiserId && normalized.hasLogo) {
      const fallback = appendAdvertiserLogoStamp(
        `${normalizeApiBase(apiBase)}/api/advertisers/${encodeURIComponent(
          normalized.advertiserId
        )}/logo/file`,
        normalized.logoUpdatedAt
      );
      if (fallback && !seen.has(fallback)) {
        seen.add(fallback);
        sources.push(fallback);
      }
    }
  });

  return sources;
};
